import React from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  LabelList,
  LineChart,
} from "recharts";

const formatCurrency = (value) => {
  return `$${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};

const formatPercentage = (value) => {
  return `${value.toFixed(2)}%`;
};

export function RevenueVsCostChart({ finalDATA }) {
  // Extract Total Revenue from formattedData
  const totalRevenueRow = finalDATA.formattedData
    .flat()
    .find((row) => row[0] === "Total Revenue");

  // Extract Total Cost of Revenue % from percentage_data
  const totalCostPercentRow = finalDATA.percentageData
    .flat()
    .find((row) => row[0] === "Total Cost of Revenue %");

  // Prepare chart data
  const chartData = totalRevenueRow.slice(1).map((rev, i) => ({
    year: `Year ${i}`,
    revenue: parseInt(rev.replace(/,/g, ""), 10),
    costPercent: parseFloat(totalCostPercentRow[i + 1].replace("%", "")),
  }));

  return (
    <ResponsiveContainer style={{margin: "0 auto"}} width="92%" height={450}>
  <ComposedChart
    data={chartData}
    margin={{ top: 30, right: 40, left: 20, bottom: 20 }}
  >
    <defs>
      {/* Gradient for Revenue Bars */}
      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#00e5ff" stopOpacity={0.9} />
        <stop offset="100%" stopColor="#0099cc" stopOpacity={0.7} />
      </linearGradient>
    </defs>

    <CartesianGrid strokeDasharray="3 3" stroke="#444" />
    <XAxis
      dataKey="year"
      tick={{ fontSize: 14, fill: "#ccc" }}
      axisLine={{ stroke: "#666" }}
    />
    <YAxis
      yAxisId="left"
      tick={{ fontSize: 14, fill: "#ccc" }}
      axisLine={{ stroke: "#666" }}
      label={{
        value: "Revenue ($)",
        angle: -90,
        position: "insideLeft",
        style: { fill: "#aaa", fontSize: 14 },
      }}
    />
    <YAxis
      yAxisId="right"
      orientation="right"
      tick={{ fontSize: 14, fill: "#ccc" }}
      axisLine={{ stroke: "#666" }}
      label={{
        value: "Cost %",
        angle: -90,
        position: "insideRight",
        style: { fill: "#aaa", fontSize: 14 },
      }}
    />

    <Tooltip
      contentStyle={{
        backgroundColor: "#1e1e2f",
        border: "1px solid #444",
        borderRadius: "8px",
        color: "#fff",
      }}
      labelStyle={{ fontWeight: "bold", color: "#fff" }}
      formatter={(value, name) =>
        name === "Revenue" ? `$${value.toLocaleString()}` : `${value}%`
      }
    />
    <Legend
      wrapperStyle={{ fontSize: 14, fontWeight: 500, color: "#fff" }}
      verticalAlign="top"
      align="center"
    />

    {/* Revenue Bar */}
    <Bar
      yAxisId="left"
      dataKey="revenue"
      fill="url(#colorRevenue)"
      barSize={60}
      radius={[10, 10, 0, 0]}
      name="Revenue"
    />

    {/* Cost of Revenue % Line */}
    <Line
      yAxisId="right"
      type="monotone"
      dataKey="costPercent"
      stroke="#bb86fc"   // purple (can swap to neon green #00ff9d)
      strokeWidth={3}
      dot={{ r: 5, fill: "#bb86fc", stroke: "#222", strokeWidth: 2 }}
      activeDot={{ r: 7 }}
      name="Cost of Revenue %"
    />
  </ComposedChart>
</ResponsiveContainer>
  );
}

export function FinancialProjectionChart({ finalDATA }) {
  // Extract relevant rows
  const revenueRow = finalDATA.formattedData
    .flat()
    .find((row) => row[0] === "Total Revenue");
  const costRow = finalDATA.formattedData
    .flat()
    .find((row) => row[0] === "Total Cost");
  const incomeRow = finalDATA.formattedData
    .flat()
    .find((row) => row[0] === "Net Income");

  if (!revenueRow || !costRow || !incomeRow)
    return <p>Missing Financial Projection data.</p>;

  // Build chart data
  const chartData = revenueRow.slice(1).map((rev, i) => ({
    year: `Year ${i}`,
    revenue: parseInt(rev.replace(/,/g, ""), 10),
    cost: parseInt(costRow[i + 1].replace(/,/g, ""), 10),
    income: parseInt(incomeRow[i + 1].replace(/,/g, ""), 10),
  }));

  return (
    <ResponsiveContainer style={{ margin: "0 auto" }} width="92%" height={420}>
      <LineChart
        data={chartData}
        margin={{ top: 40, right: 40, left: 20, bottom: 20 }}
      >
        {/* Grid */}
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />

        {/* Axes */}
        <XAxis dataKey="year" stroke="#bbb" />
        <YAxis
          stroke="#bbb"
          tickFormatter={(v) => `$${(v / 1_000_000).toFixed(1)}M`}
        />

        {/* Tooltip */}
        <Tooltip
          formatter={(value) => formatCurrency(value)}
          labelStyle={{ color: "#fff" }}
          contentStyle={{
            backgroundColor: "rgba(0,0,0,0.85)",
            borderRadius: "12px",
            border: "none",
            color: "#fff",
          }}
        />

        {/* Legend */}
        <Legend wrapperStyle={{ color: "#fff" }} />

        {/* Revenue Line */}
        <Line
          type="monotone"
          dataKey="revenue"
          name="Total Revenue"
          stroke="url(#revenueGradient)"
          strokeWidth={3}
          dot={{ r: 6, fill: "#111", stroke: "#4facfe", strokeWidth: 2 }}
          activeDot={{
            r: 8,
            stroke: "#fff",
            strokeWidth: 3,
            fill: "#4facfe",
            filter: "drop-shadow(0px 0px 6px rgba(79,172,254,0.8))",
          }}
          animationDuration={1500}
        >
          <LabelList
            dataKey="revenue"
            position="top"
            formatter={formatCurrency}
            style={{ fill: "#4facfe", fontWeight: "bold" }}
          />
        </Line>

        {/* Cost Line */}
        <Line
          type="monotone"
          dataKey="cost"
          name="Total Cost"
          stroke="url(#costGradient)"
          strokeWidth={3}
          dot={{ r: 6, fill: "#111", stroke: "#ff7300", strokeWidth: 2 }}
          activeDot={{
            r: 8,
            stroke: "#fff",
            strokeWidth: 3,
            fill: "#ff7300",
            filter: "drop-shadow(0px 0px 6px rgba(255,115,0,0.8))",
          }}
          animationDuration={1500}
        >
          <LabelList
            dataKey="cost"
            position="top"
            formatter={formatCurrency}
            style={{ fill: "#ff7300", fontWeight: "bold" }}
          />
        </Line>

        {/* Net Income Line */}
        <Line
          type="monotone"
          dataKey="income"
          name="Net Income"
          stroke="url(#incomeGradient)"
          strokeWidth={3}
          dot={{ r: 6, fill: "#111", stroke: "#52c41a", strokeWidth: 2 }}
          activeDot={{
            r: 8,
            stroke: "#fff",
            strokeWidth: 3,
            fill: "#52c41a",
            filter: "drop-shadow(0px 0px 6px rgba(82,196,26,0.8))",
          }}
          animationDuration={1500}
        >
          <LabelList
            dataKey="income"
            position="top"
            formatter={formatCurrency}
            style={{ fill: "#52c41a", fontWeight: "bold" }}
          />
        </Line>

        {/* Gradients */}
        <defs>
          <linearGradient id="revenueGradient" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#4facfe" />
            <stop offset="100%" stopColor="#00f2fe" />
          </linearGradient>
          <linearGradient id="costGradient" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#ffb347" />
            <stop offset="100%" stopColor="#ff7300" />
          </linearGradient>
          <linearGradient id="incomeGradient" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#52c41a" />
            <stop offset="100%" stopColor="#389e0d" />
          </linearGradient>
        </defs>
      </LineChart>
    </ResponsiveContainer>
  );
}

export function RevenueGrowthChart({ finalDATA }) {
  // Extract relevant rows
  const linesData = finalDATA.percentageData[1];
  
  // Build chart data
  const chartData = linesData[0].slice(1).map((rev, i) => ({
    year: `Year ${i}`,
  }));

  for (let i = 0; i < linesData.length; i++) {
    const lineName = linesData[i][0];
    chartData.forEach((dataPoint, index) => {
      dataPoint[lineName] = parseFloat(linesData[i][index + 1].replace("%", ""));
    });
  }

  return (
    <ResponsiveContainer style={{ margin: "0 auto" }} width="92%" height={420}>
      <LineChart
        data={chartData}
        margin={{ top: 40, right: 40, left: 20, bottom: 20 }}
      >
        {/* Grid */}
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />

        {/* Axes */}
        <XAxis dataKey="year" stroke="#bbb" />
        <YAxis
          stroke="#bbb"
          tickFormatter={(v) => `${v}%`}
        />

        {/* Tooltip */}
        <Tooltip
          formatter={(value) => formatPercentage(value)}
          labelStyle={{ color: "#fff" }}
          contentStyle={{
            backgroundColor: "rgba(0,0,0,0.85)",
            borderRadius: "12px",
            border: "none",
            color: "#fff",
          }}
        />

        {/* Legend */}
        <Legend wrapperStyle={{ color: "#fff" }} />
        {/* Dynamically render lines */}
        {linesData.map((line, idx) => (
          <Line
            key={line[0]}
            type="monotone"
            dataKey={line[0]}
            stroke={`hsl(${(idx * 60) % 360}, 70%, 50%)`} // Different color for each line
            strokeWidth={3}
            dot={{ r: 6, fill: "#111", stroke: `hsl(${(idx * 60) % 360}, 70%, 50%)`, strokeWidth: 2 }}
            activeDot={{
              r: 8,
              stroke: "#fff",
              strokeWidth: 3,
              fill: `hsl(${(idx * 60) % 360}, 70%, 50%)`,
              filter: `drop-shadow(0px 0px 6px hsla(${(idx * 60) % 360}, 70%, 50%, 0.8))`,
            }}
            animationDuration={1500}
          >
            <LabelList
              dataKey={line[0]}
              position="top"
              formatter={(value) => `${value}%`}
              style={{ fill: `hsl(${(idx * 60) % 360}, 70%, 50%)`, fontWeight: "bold" }}
            />
          </Line>
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

// Fixed Cost and Varible Cost will be bars
// Total Cost of Revenue % will be line
// Labor Cost % will be line
export function CostOverviewChart({ finalDATA }) {
  const fixedCostRow = finalDATA.formattedData
    .flat()
    .find((row) => row[0] === "Fixed Cost");
  const variableCostRow = finalDATA.formattedData
    .flat()
    .find((row) => row[0] === "Variable Cost");
  const totalCostRow = finalDATA.percentageData
    .flat()
    .find((row) => row[0] === "Total Cost of Revenue %");
  const laborCostRow = finalDATA.percentageData
    .flat()
    .find((row) => row[0] === "Labor Cost of Rev. %");

  if (!fixedCostRow || !variableCostRow || !totalCostRow || !laborCostRow)
    return <p>Missing Cost Overview data.</p>;

  const chartData = fixedCostRow.slice(1).map((_, i) => ({
    year: `Year ${i}`,
    fixedCost: parseInt(fixedCostRow[i + 1].replace(/,/g, ""), 10),
    variableCost: parseInt(variableCostRow[i + 1].replace(/,/g, ""), 10),
    totalCostPercent: parseFloat(totalCostRow[i + 1].replace("%", "")),
    laborCostPercent: parseFloat(laborCostRow[i + 1].replace("%", "")),
  }));

  const formatCurrency = (value) =>
    `$${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  const formatPercentage = (value) => `${value.toFixed(2)}%`;

  return (
    <ResponsiveContainer style={{ margin: "0 auto" }} width="92%" height={450}>
      <ComposedChart
        data={chartData}
        margin={{ top: 40, right: 40, left: 20, bottom: 20 }}
      >
        <defs>
          <linearGradient id="colorFixedCost" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#ff9a9e" />
            <stop offset="100%" stopColor="#fad0c4" />
          </linearGradient>
          <linearGradient id="colorVariableCost" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#a18cd1" />
            <stop offset="100%" stopColor="#fbc2eb" />
          </linearGradient>
        </defs>

        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis
          dataKey="year"
          tick={{ fontSize: 14, fill: "#ccc" }}
          axisLine={{ stroke: "#666" }}
        />
        <YAxis
          yAxisId="left"
          tick={{ fontSize: 14, fill: "#ccc" }}
          axisLine={{ stroke: "#666" }}
          label={{
            value: "Cost ($)",
            angle: -90,
            position: "insideLeft",
            style: { fill: "#aaa", fontSize: 14 },
          }}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          tick={{ fontSize: 14, fill: "#ccc" }}
          axisLine={{ stroke: "#666" }}
          label={{
            value: "Percent (%)",
            angle: -90,
            position: "insideRight",
            style: { fill: "#aaa", fontSize: 14 },
          }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: "#1e1e2f",
            border: "1px solid #444",
            borderRadius: "8px",
            color: "#fff",
          }}
          labelStyle={{ fontWeight: "bold", color: "#fff" }}
          formatter={(value, name) => {
            if (name === "Fixed Cost" || name === "Variable Cost") {
              return formatCurrency(value);
            }
            return formatPercentage(value);
          }}
        />
        <Legend
          wrapperStyle={{ fontSize: 14, fontWeight: 500, color: "#fff" }}
          verticalAlign="top"
          align="center"
        />
        <Bar
          yAxisId="left"
          dataKey="fixedCost"
          fill="url(#colorFixedCost)"
          radius={[10, 10, 0, 0]}
          name="Fixed Cost"
          barSize={40}
        />
        <Bar
          yAxisId="left"
          dataKey="variableCost"
          fill="url(#colorVariableCost)"
          radius={[10, 10, 0, 0]}
          name="Variable Cost"
          barSize={40}
        />
        <Line
          yAxisId="right"
          dataKey="totalCostPercent"
          stroke="#ff7300"
          strokeWidth={3}
          dot={{ r: 6, fill: "#111", stroke: "#ff7300", strokeWidth: 2 }}
          activeDot={{
            r: 8,
            stroke: "#fff",
            strokeWidth: 3,
            fill: "#ff7300",
            filter: "drop-shadow(0px 0px 6px rgba(255,115,0,0.8))",
          }}
          name="Total Cost of Revenue %"
          animationDuration={1500}
        />
        <Line
          yAxisId="right"
          dataKey="laborCostPercent"
          stroke="#00c49f"
          strokeWidth={3}
          dot={{ r: 6, fill: "#111", stroke: "#00c49f", strokeWidth: 2 }}
          activeDot={{
            r: 8,
            stroke: "#fff",
            strokeWidth: 3,
            fill: "#00c49f",
            filter: "drop-shadow(0px 0px 6px rgba(0,196,159,0.8))",
          }}
          name="Labor Cost %"
          animationDuration={1500}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function BurnRateChart({ finalDATA }) {
  // Find the relevant rows
  const burnRateRow = finalDATA.formattedData
    .flat()
    .find((row) => row[0] === "Monthly Burn Rate");
  const burnRatePercentRow = finalDATA.percentageData
    .flat()
    .find((row) => row[0] === "Burn Rate Usage %");

  if (!burnRateRow || !burnRatePercentRow)
    return <p>Missing Burn Rate data.</p>;

  // Prepare chart data
  const chartData = burnRateRow.slice(1).map((_, i) => ({
    year: `Year ${i}`,
    burnRate: parseInt(burnRateRow[i + 1].replace(/,/g, ""), 10),
    burnRatePercent: parseFloat(burnRatePercentRow[i + 1].replace("%", "")),
  }));

  const formatCurrency = (value) =>
    `$${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  const formatPercentage = (value) => `${value.toFixed(2)}%`;

  return (
    <ResponsiveContainer style={{ margin: "0 auto" }} width="92%" height={420}>
      <ComposedChart
        data={chartData}
        margin={{ top: 40, right: 40, left: 20, bottom: 20 }}
      >
        <defs>
          <linearGradient id="colorBurnRate" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#ff5858" />
            <stop offset="100%" stopColor="#ffc371" />
          </linearGradient>
        </defs>

        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis
          dataKey="year"
          tick={{ fontSize: 14, fill: "#ccc" }}
          axisLine={{ stroke: "#666" }}
        />
        <YAxis
          yAxisId="left"
          tick={{ fontSize: 14, fill: "#ccc" }}
          axisLine={{ stroke: "#666" }}
          label={{
            value: "Burn Rate ($)",
            angle: -90,
            position: "insideLeft",
            style: { fill: "#aaa", fontSize: 14 },
          }}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          tick={{ fontSize: 14, fill: "#ccc" }}
          axisLine={{ stroke: "#666" }}
          label={{
            value: "Burn Rate Usage (%)",
            angle: -90,
            position: "insideRight",
            style: { fill: "#aaa", fontSize: 14 },
          }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: "#1e1e2f",
            border: "1px solid #444",
            borderRadius: "8px",
            color: "#fff",
          }}
          labelStyle={{ fontWeight: "bold", color: "#fff" }}
          formatter={(value, name) => {
            if (name === "Burn Rate") {
              return formatCurrency(value);
            }
            return formatPercentage(value);
          }}
        />
        <Legend
          wrapperStyle={{ fontSize: 14, fontWeight: 500, color: "#fff" }}
          verticalAlign="top"
          align="center"
        />
        <Bar
          yAxisId="left"
          dataKey="burnRate"
          fill="url(#colorBurnRate)"
          radius={[10, 10, 0, 0]}
          name="Burn Rate"
          barSize={50}
        />
        <Line
          yAxisId="right"
          dataKey="burnRatePercent"
          stroke="#ff5858"
          strokeWidth={3}
          dot={{ r: 6, fill: "#111", stroke: "#ff5858", strokeWidth: 2 }}
          activeDot={{
            r: 8,
            stroke: "#fff",
            strokeWidth: 3,
            fill: "#ff5858",
            filter: "drop-shadow(0px 0px 6px rgba(255,88,88,0.8))",
          }}
          name="Burn Rate Usage %"
          animationDuration={1500}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}